/**
 * The page a reset link lands on. Reads the token out of the URL, takes the
 * new password, and on success sends the person to sign in with it — the API
 * deliberately does not start a session here (see auth.reset_password).
 */
import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import AuthCard, { AuthMessage, INPUT_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS } from '../components/AuthCard';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const { message } = await api.resetPassword(token, password);
      navigate('/login', { state: { message } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthCard title="Reset password">
        <AuthMessage tone="error">This reset link is missing its token.</AuthMessage>
        <p className="text-center text-sm text-gray-600 dark:text-gray-300">
          <Link to="/forgot-password" className="text-blue-600 dark:text-blue-400 hover:text-blue-500">
            Request a new link
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <AuthMessage tone="error">
            {error}{' '}
            <Link to="/forgot-password" className="underline hover:no-underline">
              Request a new link
            </Link>
          </AuthMessage>
        )}
        <div>
          <label htmlFor="password" className={LABEL_CLASS}>
            New password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="confirmPassword" className={LABEL_CLASS}>
            Confirm new password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className={INPUT_CLASS}
          />
        </div>
        <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
          {loading ? 'Saving...' : 'Set password'}
        </button>
      </form>
    </AuthCard>
  );
}
